import React, { useState } from 'react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { ToggleExpandList } from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalScorerMarket.state';
import {
    Caption,
    GoalScorerMarketListItem,
    GoalScorerMarketListWrapper,
    GoalscorerName,
    MarketGroupCaption,
} from 'src/domains/sportsbook/webview/components/goalscorerMarketGroupUnified/GoalScorerMarket.style';
import {
    GoalscorerColumnType,
    GoalScorerMarketState,
    GoalScorerType,
} from 'src/domains/sportsbook/webview/components/goalscorerMarketGroupUnified/GoalScorerMarket.state';
import { Selection } from 'src/domains/sportsbook/webview/components/Selection';
import { observer } from 'src/utils/mobx-react';

export interface GoalScorerMarketPropsType {
    state: GoalScorerMarketState;
}

export const MARKET_NAMES_UNIFIED: Record<string, JSX.Element> = {
    'first-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.first'
            defaultText='First'
        />
    ),
    'anytime-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.anytime'
            defaultText='Anytime'
        />
    ),
    'hattrick-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.hattrick'
            defaultText='Hat-trick'
        />
    ),
    'last-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.last'
            defaultText='Last'
        />
    ),
};

const COLUMN_TO_MARKET_NAME: Record<GoalscorerColumnType, string> = {
    first: 'first-goalscorer',
    anytime: 'anytime-goalscorer',
    hatTrick: 'hattrick-goalscorer',
    last: 'last-goalscorer',
};

const getActiveColumns = (state: GoalScorerMarketState): Array<GoalscorerColumnType> => {
    const players = [...state.goalScorerListHome, ...state.goalScorerListAway];
    if (state.noGoalScore !== null) {
        players.push(state.noGoalScore);
    }

    const columns: Array<GoalscorerColumnType> = [];
    if (players.some((player) => player.first !== null)) {
        columns.push('first');
    }
    if (players.some((player) => player.anytime !== null)) {
        columns.push('anytime');
    }
    if (players.some((player) => player.hatTrick !== null)) {
        columns.push('hatTrick');
    }
    if (players.some((player) => player.last !== null)) {
        columns.push('last');
    }

    // Fallback for first-goalscorer variants if column detection missed loaded selections
    if (columns.length === 0 && players.length > 0) {
        columns.push('first');
    }

    return columns;
};

interface GoalScorerMarketListItemWrapperPropsType {
    goalScorer: GoalScorerType;
    columns: Array<GoalscorerColumnType>;
}

export const GoalScorerMarketListItemWrapper = observer(
    'GoalScorerMarketListItemWrapper',
    (props: GoalScorerMarketListItemWrapperPropsType): JSX.Element | null => {
        const {
            goalScorer: { name, first, anytime, hatTrick, last },
            columns,
        } = props;

        const selectionByColumn: Record<GoalscorerColumnType, typeof first> = {
            first,
            anytime,
            hatTrick,
            last,
        };

        return (
            <GoalScorerMarketListItem
                key={name}
                columns={columns.length + 1}
            >
                <GoalscorerName>
                    {name.toLowerCase() === 'no goalscorer' ? (
                        <I18n
                            langKey='selection.football.match.no-goalscorer'
                            defaultText='No goalscorer'
                        />
                    ) : (
                        <span>{name}</span>
                    )}
                </GoalscorerName>
                {columns.map((column) => (
                    <Selection
                        key={column}
                        selectionId={selectionByColumn[column] ?? undefined}
                    />
                ))}
            </GoalScorerMarketListItem>
        );
    }
);

export const GoalScorerMarket = observer('GoalScorerMarket', ({ state }: GoalScorerMarketPropsType) => {
    const [toggleState] = useState(() => new ToggleExpandList());
    const goalScorerList = [...state.goalScorerListHome, ...state.goalScorerListAway];
    const { isExpanded, toggleExpand } = toggleState;
    const columns = getActiveColumns(state);

    return (
        <>
            <GoalScorerMarketListWrapper>
                {goalScorerList.length > 0 ? (
                    <MarketGroupCaption>
                        <Caption>
                            <I18n
                                langKey='events.goalscorer-market.players'
                                defaultText='Players'
                            />
                        </Caption>
                        {columns.map((column) => {
                            const marketNameKey = COLUMN_TO_MARKET_NAME[column];
                            const marketName = MARKET_NAMES_UNIFIED[marketNameKey];
                            return <Caption key={column}>{marketName}</Caption>;
                        })}
                    </MarketGroupCaption>
                ) : null}

                {goalScorerList.slice(0, isExpanded === true ? goalScorerList.length : 6).map((elem) => {
                    return (
                        <GoalScorerMarketListItemWrapper
                            key={elem.name}
                            goalScorer={elem}
                            columns={columns}
                        />
                    );
                })}
            </GoalScorerMarketListWrapper>

            {state.noGoalScore !== null && (isExpanded || state.isPossibleToExpandList === false) ? (
                <GoalScorerMarketListItemWrapper
                    goalScorer={state.noGoalScore}
                    columns={columns}
                />
            ) : null}

            {state.isPossibleToExpandList ? (
                <ExpandButton
                    isExpanded={isExpanded}
                    toggleExpand={toggleExpand}
                />
            ) : null}
        </>
    );
});
